import { CloseIcon, getPlan, live, SearchIcon, type Plan } from '@trippath/shared';
import { useEffect, useId, useRef, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { day } from '../lib/format';

/** A thing of the trip found by what is typed: what it is called, what it is, where it opens. */
interface Found {
  key: string;
  label: string;
  detail: string;
  to: string;
}

/** How many are offered at most, before the map's own search. */
const MOST = 8;

/** Whether every word typed is in `text`, as typed in any case. */
function fits(text: string, words: readonly string[]): boolean {
  const lower = text.toLowerCase();
  return words.every((word) => lower.includes(word));
}

/** The trip's stops, bookings and things to do that say what is typed, the stops first. */
export function foundIn(plan: Plan, typed: string, lang: string, t: TFunction): Found[] {
  const words = typed
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word !== '');
  if (words.length === 0) return [];
  const base = `/trips/${plan.trip.id}`;
  const stops = plan.stops
    .filter((stop) => fits(stop.city, words))
    .map((stop) => ({
      key: `stop-${stop.id}`,
      label: stop.city,
      detail: t('tabs.searchStop', { from: day(stop.start, lang), to: day(stop.end, lang) }),
      to: `${base}?stop=${stop.id}`,
    }));
  const bookings = plan.stops.flatMap((stop) =>
    stop.bookings
      .filter((booking) => live(booking) && fits(booking.name, words))
      .map((booking) => ({
        key: `booking-${booking.id}`,
        label: booking.name,
        detail: t('tabs.searchBooking', { city: stop.city }),
        to: `${base}?stop=${stop.id}`,
      })),
  );
  const things = plan.stops.flatMap((stop) =>
    stop.agenda
      .filter((thing) => fits(thing.name, words))
      .map((thing) => ({
        key: `thing-${thing.id}`,
        label: thing.name,
        detail: t('tabs.searchThing', { city: stop.city }),
        to: `/trips/${plan.trip.id}/stops/${stop.id}?day=${thing.day}`,
      })),
  );
  return [...stops, ...bookings, ...things].slice(0, MOST);
}

/**
 * The bar's search, a magnifying glass until pressed: then a field, and under it what of the
 * trip says what is typed — its stops, bookings and things to do — and last the map's own search
 * for anything else (`HEL-BKK`, a city not on the route). The ARIA combobox pattern: the arrows
 * move through the list, Enter opens one, Escape closes it.
 */
export function BarSearch({ trip }: { trip: string }) {
  const { t, i18n } = useTranslation();
  const ids = useId();
  const navigate = useNavigate();
  const field = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [active, setActive] = useState(0);
  const [plan, setPlan] = useState<Plan | null>(null);

  // The trip as it is now, read each time the search is opened.
  useEffect(() => {
    if (!open) return;
    getPlan(trip).then(setPlan, () => undefined);
    field.current?.focus();
  }, [open, trip]);

  const typed = text.trim();
  // Another trip's, read before this one was opened, is not this one's.
  const found = plan?.trip.id === trip ? foundIn(plan, typed, i18n.language, t) : [];
  const options: Found[] =
    typed === ''
      ? []
      : [
          ...found,
          {
            key: 'map',
            label: t('tabs.searchMap', { text: typed }),
            detail: '',
            to: `/trips/${trip}?q=${encodeURIComponent(typed)}`,
          },
        ];
  const close = () => {
    setOpen(false);
    setText('');
    setActive(0);
  };
  const go = (option: Found | undefined) => {
    if (!option) return;
    close();
    void navigate(option.to);
  };

  if (!open) {
    return (
      <button
        type="button"
        className="bar-icon bar-search-open"
        aria-label={t('tabs.searchOpen')}
        title={t('tabs.searchOpen')}
        onClick={() => {
          setOpen(true);
        }}
      >
        <SearchIcon size={18} />
      </button>
    );
  }
  const list = `${ids}-list`;
  return (
    <div className="bar-search" role="search">
      <SearchIcon size={16} />
      <input
        ref={field}
        type="text"
        role="combobox"
        aria-label={t('search.label')}
        aria-expanded={options.length > 0}
        aria-controls={list}
        aria-autocomplete="list"
        aria-activedescendant={options[active] ? `${ids}-${options[active].key}` : undefined}
        placeholder={t('tabs.search')}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setActive(0);
        }}
        // Left, it closes; a press on its list keeps the field focused until one is chosen.
        onBlur={close}
        onKeyDown={(event) => {
          if (event.key === 'Escape') close();
          else if (event.key === 'Enter') {
            event.preventDefault();
            go(options[active]);
          } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setActive((was) => (was + step + options.length) % Math.max(1, options.length));
          }
        }}
      />
      <button
        type="button"
        className="bar-search-close"
        aria-label={t('dialog.close')}
        title={t('dialog.close')}
        // Pressed, the field keeps its focus until the search is closed.
        onMouseDown={(event) => {
          event.preventDefault();
        }}
        onClick={close}
      >
        <CloseIcon />
      </button>
      {options.length > 0 && (
        <ul
          className="bar-search-list"
          id={list}
          role="listbox"
          aria-label={t('tabs.searchOpen')}
        >
          {options.map((option, at) => (
            <li
              key={option.key}
              id={`${ids}-${option.key}`}
              role="option"
              aria-selected={at === active}
              tabIndex={-1}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={() => {
                go(option);
              }}
            >
              <span className="bar-search-name">{option.label}</span>
              {option.detail !== '' && <span className="quiet">{option.detail}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
